import { ArrowLeft,ArrowRight,KeyRound,LockKeyhole,Mail,RotateCw,ShieldCheck } from 'lucide-react';
import { useRef,useState,type ClipboardEvent,type FormEvent,type KeyboardEvent } from 'react';
import { Link,Navigate,useLocation,useNavigate,useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Alert,Button,Field } from '../components/UI';
import { api,errorText } from '../lib/api';
import type { MfaChallenge } from '../types';

export function LoginPage(){
  const {user,login,verifyMfa}=useAuth();
  const navigate=useNavigate();
  const location=useLocation();
  const [mode,setMode]=useState<'login'|'forgot'>('login');
  const [email,setEmail]=useState('');
  const [password,setPassword]=useState('');
  const [code,setCode]=useState('');
  const [challenge,setChallenge]=useState<MfaChallenge|null>(null);
  const [busy,setBusy]=useState(false);
  const [resendBusy,setResendBusy]=useState(false);
  const [error,setError]=useState('');
  const [message,setMessage]=useState('');
  const codeRefs=useRef<Array<HTMLInputElement|null>>([]);
  const destination=(location.state as {from?:string})?.from||'/dashboard';

  if(user)return <Navigate to="/dashboard" replace/>;

  const submitPassword=async(e:FormEvent)=>{
    e.preventDefault();setBusy(true);setError('');setMessage('');
    try{
      const result=await login(email,password);
      if('challenge_id' in result){setChallenge(result);setMessage('We sent a fresh six-digit code to the email address on this account.');return;}
      navigate(destination,{replace:true});
    }catch(err){setError(errorText(err));}
    finally{setBusy(false);}
  };

  const submitCode=async(e:FormEvent)=>{
    e.preventDefault();if(!challenge)return;setBusy(true);setError('');setMessage('');
    try{await verifyMfa(challenge.challenge_id,code);navigate(destination,{replace:true});}
    catch(err){setError(errorText(err));}
    finally{setBusy(false);}
  };

  const resendCode=async()=>{
    if(!challenge)return;setResendBusy(true);setError('');setMessage('');
    try{
      const response=await api.post<MfaChallenge>('/auth/mfa/resend',{challenge_id:challenge.challenge_id});
      setChallenge(response.data);setCode('');setMessage('A new verification code has been sent.');
    }catch(err){setError(errorText(err));}
    finally{setResendBusy(false);}
  };

  const forgotPassword=async(e:FormEvent)=>{
    e.preventDefault();setBusy(true);setError('');setMessage('');
    try{
      await api.post('/auth/forgot-password',{email,client:'admin'});
      setMessage('If that address belongs to a Pintop admin account, a password reset link has been sent.');
    }catch(err){setError(errorText(err));}
    finally{setBusy(false);}
  };

  const digits=Array.from({length:6},(_,index)=>code[index]||'');
  const updateDigit=(index:number,value:string)=>{
    const digit=value.replace(/\D/g,'').slice(-1);
    const next=[...digits];
    next[index]=digit;
    setCode(next.join('').slice(0,6));
    if(digit&&index<5)codeRefs.current[index+1]?.focus();
  };
  const pasteCode=(event:ClipboardEvent<HTMLInputElement>)=>{
    const pasted=event.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if(!pasted)return;
    event.preventDefault();
    setCode(pasted);
    codeRefs.current[Math.min(pasted.length,6)-1]?.focus();
  };
  const codeKey=(index:number,event:KeyboardEvent<HTMLInputElement>)=>{
    if(event.key==='Backspace'&&!digits[index]&&index>0)codeRefs.current[index-1]?.focus();
    if(event.key==='ArrowLeft'&&index>0)codeRefs.current[index-1]?.focus();
    if(event.key==='ArrowRight'&&index<5)codeRefs.current[index+1]?.focus();
  };

  return <main className="login-page">
    <section className="login-story">
      <div className="brand light"><img className="brand-logo-full" src="/brand/pintop-logo.png" alt="Pintop Technologies"/><div>
        {/* <strong>Pintop Technologies</strong><span>Training Management Platform</span> */}
        </div></div>
      <div><span className="eyebrow light">Pintop management</span><h1>Manage every learner journey without juggling five spreadsheets.</h1><p>Admissions, curricula, cohorts, live classes, grading, finance, certificates and corporate delivery live in one trusted place.</p></div>
      <ul><li><ShieldCheck/> Role-scoped operational access</li><li><LockKeyhole/> Protected academic and financial records</li></ul>
    </section>
    <section className="login-panel">
      {!challenge&&mode==='login'?<form onSubmit={submitPassword} autoComplete="off">
        <span className="eyebrow">Authorised personnel</span>
        <h2>Sign in to management</h2>
        <p>Use a Pintop staff account assigned to an administrative role.</p>
        {error&&<Alert>{error}</Alert>}
        {message&&<Alert type="success">{message}</Alert>}
        <Field label="Email address"><input className="form-control form-control-lg" type="email" value={email} onChange={e=>setEmail(e.target.value)} required autoFocus autoComplete="off"/></Field>
        <Field label="Password"><input className="form-control form-control-lg" type="password" value={password} onChange={e=>setPassword(e.target.value)} required autoComplete="new-password"/></Field>
        <button className="text-button justify-content-end w-100" type="button" onClick={()=>{setMode('forgot');setError('');setMessage('');}}>Forgot password?</button>
        <Button type="submit" busy={busy}>Continue <ArrowRight size={17}/></Button>
      </form>:!challenge?<form onSubmit={forgotPassword} autoComplete="off">
        <span className="eyebrow">Account recovery</span>
        <h2>Reset your password</h2>
        <p>Enter your Pintop admin email address and we will send a secure reset link.</p>
        {error&&<Alert>{error}</Alert>}
        {message&&<Alert type="success">{message}</Alert>}
        <Field label="Email address"><input className="form-control form-control-lg" type="email" value={email} onChange={e=>setEmail(e.target.value)} required autoFocus autoComplete="off"/></Field>
        <Button type="submit" busy={busy}>{message?'Resend reset link':'Send reset link'} <Mail size={17}/></Button>
        <button className="text-button" type="button" onClick={()=>{setMode('login');setError('');setMessage('');}}><ArrowLeft size={15}/> Back to sign in</button>
      </form>:<form onSubmit={submitCode}>
        <span className="eyebrow">Second verification</span>
        <h2>Enter your access code</h2>
        <p>We sent a six-digit code to the email address for this privileged account.</p>
        {error&&<Alert>{error}</Alert>}
        {message&&<Alert type="success">{message}</Alert>}
        <Field label="Verification code">
          <div className="otp-input-grid" role="group" aria-label="Six digit verification code">
            {digits.map((digit,index)=><input key={index} ref={node=>{codeRefs.current[index]=node;}} className="form-control form-control-lg otp-digit" inputMode="numeric" pattern="[0-9]" maxLength={1} value={digit} onChange={e=>updateDigit(index,e.target.value)} onPaste={pasteCode} onKeyDown={e=>codeKey(index,e)} required autoFocus={index===0} aria-label={`Digit ${index+1}`}/>)}
          </div>
          <input type="hidden" value={code} required pattern="[0-9]{6}"/>
        </Field>
        <Button type="submit" busy={busy}>Verify and sign in <KeyRound size={17}/></Button>
        <Button kind="secondary" type="button" busy={resendBusy} onClick={()=>void resendCode()}><RotateCw size={16}/> Resend code</Button>
        <button className="text-button" type="button" onClick={()=>{setChallenge(null);setCode('');setError('');}}><ArrowLeft size={15}/> Use a different account</button>
      </form>}
    </section>
  </main>;
}

export function ResetPasswordPage(){
  const navigate=useNavigate();
  const [params]=useSearchParams();
  const email=(params.get('email')||'').trim().toLowerCase();
  const token=(params.get('token')||'').trim();
  const [password,setPassword]=useState('');
  const [confirmation,setConfirmation]=useState('');
  const [busy,setBusy]=useState(false);
  const [resendBusy,setResendBusy]=useState(false);
  const [error,setError]=useState('');
  const [message,setMessage]=useState('');
  const missingToken=!token;
  const missingSecureLink=!email||!token;

  const submit=async(e:FormEvent)=>{
    e.preventDefault();setBusy(true);setError('');setMessage('');
    try{
      await api.post('/auth/reset-password',{email,token,password,password_confirmation:confirmation});
      setMessage('Your password has been reset. You can now sign in with the new password.');
      setPassword('');setConfirmation('');
      setTimeout(()=>navigate('/login',{replace:true}),1200);
    }catch(err){setError(errorText(err));}
    finally{setBusy(false);}
  };

  const resend=async()=>{
    setResendBusy(true);setError('');setMessage('');
    try{
      await api.post('/auth/forgot-password/resend',{email,client:'admin'});
      setMessage('If that address belongs to a Pintop admin account, a new reset link has been sent.');
    }catch(err){setError(errorText(err));}
    finally{setResendBusy(false);}
  };

  return <main className="login-page">
    <section className="login-story">
      <div className="brand light"><img className="brand-logo-full" src="/brand/pintop-logo.png" alt="Pintop Technologies"/><div>
        {/* <strong>Pintop Technologies</strong><span>Training Management Platform</span> */}
        </div></div>
      <div><span className="eyebrow light">Secure recovery</span><h1>Reset access without weakening account security.</h1><p>Use the secure link from your email to create a new password for your Pintop management account.</p></div>
      <ul><li><ShieldCheck/> Token-based reset verification</li><li><LockKeyhole/> Active sessions are revoked after reset</li></ul>
    </section>
    <section className="login-panel">
      <form onSubmit={submit} autoComplete="off">
        <span className="eyebrow">Password reset</span>
        <h2>Create a new password</h2>
        <p>Passwords must be at least 10 characters and include mixed case, numbers and symbols.</p>
        {missingSecureLink&&<Alert>This reset link is missing secure account details. Please request a fresh reset link.</Alert>}
        {error&&<Alert>{error}</Alert>}
        {message&&<Alert type="success">{message}</Alert>}
        <input type="hidden" value={email} readOnly required/>
        <input type="hidden" value={token} readOnly required/>
        <Field label="New password"><input className="form-control form-control-lg" type="password" value={password} onChange={e=>setPassword(e.target.value)} required minLength={10} autoComplete="new-password"/></Field>
        <Field label="Confirm password"><input className="form-control form-control-lg" type="password" value={confirmation} onChange={e=>setConfirmation(e.target.value)} required minLength={10} autoComplete="new-password"/></Field>
        <Button type="submit" busy={busy} disabled={missingSecureLink}>Reset password <KeyRound size={17}/></Button>
        <Button kind="secondary" type="button" busy={resendBusy} disabled={!email} onClick={()=>void resend()}><RotateCw size={16}/> Resend reset link</Button>
        <Link className="text-button" to="/login"><ArrowLeft size={15}/> Back to sign in</Link>
      </form>
    </section>
  </main>;
}
